(() => {
  if (window.kpoFlexibleDateInitialized) return;
  window.kpoFlexibleDateInitialized = true;

  const TARGET_SELECTOR = '.kpo-flexible-date';
  const INPUT_SELECTOR = 'input:not([type]), input[type="text"], input[type="search"], input[type="tel"], textarea';
  const FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';
  const FORM_CLASS = 'kpo-flexible-date-form';
  const INVALID_CLASS = 'kpo-flexible-date__invalid';
  const states = new Map();
  let guardedForms = new Set();
  let scheduledRefresh = false;
  let errorId = 0;

  function isCalendarDate(year, month, day) {
    if (year < 1 || month < 1 || month > 12 || day < 1) return false;
    const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const monthLengths = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= monthLengths[month - 1];
  }

  function isValidDate(value) {
    const text = value.trim();
    if (!text) return true;

    const match = text.match(/^(\d{1,4})\s*([.\/-])\s*(\d{1,2})\s*\2\s*(\d{1,4})$/);
    if (!match) return false;

    const [, first, , middle, last] = match;
    let year;
    let left;
    let right;

    if (first.length === 4 && last.length <= 2) {
      year = Number(first);
      left = Number(middle);
      right = Number(last);
    } else if (first.length <= 2 && (last.length === 2 || last.length === 4)) {
      year = Number(last) + (last.length === 2 ? 2000 : 0);
      left = Number(first);
      right = Number(middle);
    } else {
      return false;
    }

    return isCalendarDate(year, left, right) || isCalendarDate(year, right, left);
  }

  function findInput(target) {
    if (target.matches(INPUT_SELECTOR)) return target;
    const inputs = Array.from(target.querySelectorAll(INPUT_SELECTOR));
    return inputs.find((input) => /^properties\[/.test(input.name)) || inputs[0];
  }

  function findForm(input) {
    if (input.form?.matches(FORM_SELECTOR)) return input.form;
    const root = input.closest('product-info') || input.closest('.shopify-section');
    return root?.querySelector(FORM_SELECTOR) || null;
  }

  function isActive(input) {
    if (!input.isConnected || input.matches(':disabled')) return false;
    if (input.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
    const visibility = window.getComputedStyle(input).visibility;
    return visibility !== 'hidden' && visibility !== 'collapse' && input.getClientRects().length > 0;
  }

  function setError(state, invalid) {
    const { input, error } = state;
    if (invalid) {
      if (!state.invalid) state.previousAriaInvalid = input.getAttribute('aria-invalid');
      if (input.getAttribute('aria-invalid') !== 'true') input.setAttribute('aria-invalid', 'true');
      input.classList.add(INVALID_CLASS);
      const describedBy = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (!describedBy.includes(error.id)) {
        input.setAttribute('aria-describedby', [...describedBy, error.id].join(' '));
      }
      if (error.hidden) error.hidden = false;
    } else if (state.invalid) {
      if (input.getAttribute('aria-invalid') === 'true') {
        if (state.previousAriaInvalid === null) input.removeAttribute('aria-invalid');
        else input.setAttribute('aria-invalid', state.previousAriaInvalid);
      }
      input.classList.remove(INVALID_CLASS);
      const describedBy = (input.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && id !== error.id);
      if (describedBy.length) input.setAttribute('aria-describedby', describedBy.join(' '));
      else input.removeAttribute('aria-describedby');
      error.hidden = true;
    }
    state.invalid = invalid;
  }

  function enhance(input) {
    const error = document.createElement('p');
    do {
      error.id = `kpo-flexible-date-error-${++errorId}`;
    } while (document.getElementById(error.id));
    error.className = 'kpo-flexible-date__error';
    error.hidden = true;
    error.setAttribute('aria-live', 'polite');
    const messageRoot = input.closest('[data-kpo-flexible-date-error]') ||
      findForm(input)?.closest('[data-kpo-flexible-date-error]') ||
      document.querySelector('[data-kpo-flexible-date-error]');
    error.textContent = messageRoot?.dataset.kpoFlexibleDateError || '';
    input.insertAdjacentElement('afterend', error);
    const state = { input, error, invalid: false, previousAriaInvalid: null };
    states.set(input, state);
    return state;
  }

  function refresh() {
    const inputs = new Set();
    const forms = new Set();
    document.querySelectorAll(TARGET_SELECTOR).forEach((target) => {
      const input = findInput(target);
      if (!input) return;
      inputs.add(input);
      const state = states.get(input) || enhance(input);
      if (input.nextElementSibling !== state.error) input.insertAdjacentElement('afterend', state.error);
      if (!isActive(input)) setError(state, false);
      const form = findForm(input);
      if (form) forms.add(form);
    });

    states.forEach((state, input) => {
      if (inputs.has(input)) return;
      setError(state, false);
      state.error.remove();
      states.delete(input);
    });
    guardedForms.forEach((form) => {
      if (!forms.has(form)) form.classList.remove(FORM_CLASS);
    });
    forms.forEach((form) => {
      if (!form.classList.contains(FORM_CLASS)) form.classList.add(FORM_CLASS);
    });
    guardedForms = forms;
  }

  function scheduleRefresh() {
    if (scheduledRefresh) return;
    scheduledRefresh = true;
    window.requestAnimationFrame(() => {
      scheduledRefresh = false;
      refresh();
    });
  }

  function validateForm(form) {
    refresh();
    let firstInvalid = null;
    states.forEach((state, input) => {
      if (findForm(input) !== form) return;
      const invalid = isActive(input) && !isValidDate(input.value);
      setError(state, invalid);
      if (invalid && !firstInvalid) firstInvalid = input;
    });
    return firstInvalid;
  }

  function guardPurchase(event, form) {
    if (!form?.matches(FORM_SELECTOR)) return;
    const firstInvalid = validateForm(form);
    if (!firstInvalid) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    firstInvalid.focus();
  }

  function guardPurchaseButton(event) {
    if (event.button !== 0) return;
    const button = event.target.closest?.('button, input[type="submit"], input[type="image"]');
    if (!button || (button.type !== 'submit' && button.type !== 'image' && button.name !== 'add')) return;
    guardPurchase(event, button.form || button.closest('product-form')?.querySelector(FORM_SELECTOR));
  }

  document.addEventListener('submit', (event) => guardPurchase(event, event.target), true);
  document.addEventListener('pointerdown', guardPurchaseButton, true);
  document.addEventListener('click', guardPurchaseButton, true);
  document.addEventListener('input', (event) => {
    const state = states.get(event.target);
    if (state) setError(state, false);
  }, true);
  document.addEventListener('blur', (event) => {
    if (!event.target.matches?.(INPUT_SELECTOR) || !event.target.closest(TARGET_SELECTOR)) return;
    refresh();
    const state = states.get(event.target);
    if (state) setError(state, isActive(state.input) && !isValidDate(state.input.value));
  }, true);
  document.addEventListener('reset', (event) => {
    states.forEach((state, input) => {
      if (findForm(input) === event.target) setError(state, false);
    });
  }, true);
  document.addEventListener('shopify:section:load', scheduleRefresh);

  function init() {
    refresh();
    new MutationObserver(scheduleRefresh).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'disabled', 'aria-hidden', 'inert', 'type', 'form', 'id'],
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
